import type { MedplumClient } from '@medplum/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { executeChimeTelehealth, type ChimeRecordingResult } from './chimeTelehealthService';

/** Recording state, provider presence and the waiting room, refreshed in the call. */
const SESSION_STATUS_POLL_MS = 5_000;

/**
 * Skips a poll while the previous one is still running, and drops a response once a newer
 * status was applied (another poll, or a `recording-start` / `recording-stop` result), so a
 * slow poll cannot overwrite fresher state. `refresh` always fetches: it supersedes a poll
 * still in flight (e.g. one sent before an admit) instead of waiting on its older answer.
 */
export function createLatestStatusGate<T>(
  fetchStatus: () => Promise<T>,
  apply: (status: T) => void,
): { poll: () => Promise<void>; refresh: () => Promise<void>; set: (status: T) => void } {
  let sequence = 0;
  let inFlight = 0;
  const fetchLatest = async (): Promise<void> => {
    inFlight += 1;
    const mine = ++sequence;
    try {
      const status = await fetchStatus();
      if (mine === sequence) apply(status);
    } catch {
      /* next poll retries */
    } finally {
      inFlight -= 1;
    }
  };
  return {
    async poll() {
      if (inFlight > 0) return;
      await fetchLatest();
    },
    refresh: fetchLatest,
    set(status) {
      sequence += 1;
      apply(status);
    },
  };
}

/**
 * Carries the previous poll's `waiting` list onto a result that has none (the immediate response
 * from `provider-join` / `recording-start` / `recording-stop`), so the waiting room doesn't blink
 * empty until the next 5s `session-status` poll.
 */
export function withPreservedWaiting(
  next: ChimeRecordingResult,
  previous: ChimeRecordingResult | undefined,
): ChimeRecordingResult {
  return next.waiting !== undefined || !previous?.waiting ? next : { ...next, waiting: previous.waiting };
}

/**
 * Polls `session-status` while `enabled` (once right away, then every few seconds); `refresh`
 * fetches now, superseding a poll in flight. `setStatus` applies a recording action's or join's result. The status is cleared
 * when `enabled` turns off, so a leftover status from an earlier call never outlives it.
 */
export function useChimeSessionStatus(
  medplum: MedplumClient,
  appointmentId: string | undefined,
  enabled: boolean,
): {
  status: ChimeRecordingResult | undefined;
  setStatus: (status: ChimeRecordingResult) => void;
  refresh: () => Promise<void>;
} {
  const [status, applyStatus] = useState<ChimeRecordingResult>();
  // Mirrors `status` for use inside the stable `setStatus` callback below without retriggering it.
  const statusRef = useRef(status);
  statusRef.current = status;
  const gate = useMemo(
    () =>
      createLatestStatusGate<ChimeRecordingResult | undefined>(
        () =>
          executeChimeTelehealth(medplum, {
            action: 'session-status',
            appointmentId,
          }) as Promise<ChimeRecordingResult>,
        applyStatus,
      ),
    [appointmentId, medplum],
  );

  useEffect(() => {
    if (!enabled || !appointmentId) return undefined;
    void gate.poll();
    const id = setInterval(() => void gate.poll(), SESSION_STATUS_POLL_MS);
    return () => {
      clearInterval(id);
      // Also drops a poll still in flight.
      gate.set(undefined);
    };
  }, [appointmentId, enabled, gate]);

  const setStatus = useCallback(
    (next: ChimeRecordingResult) => gate.set(withPreservedWaiting(next, statusRef.current)),
    [gate],
  );

  return { status, setStatus, refresh: gate.refresh };
}
